// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import {
    IconButton, ITooltipHostStyles, PrimaryButton,
    Spinner, SpinnerSize, TooltipHost
} from "@fluentui/react";
import _ from "lodash";
import { Feature } from "ol";
import Polygon from "ol/geom/Polygon";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import pdfjsLib from "pdfjs-dist";
import React from "react";
import { constants } from "../../common/constants";
import { getPrimaryGreyTheme, getPrimaryGreenTheme } from "../../common/themes";
import { ImageMap } from "../../Components/ImageMap";
import { CanvasCommandBar } from "../../Components/CommandBar";
import { TableView } from "../TableView";
import { ILoadFileHelper, ILoadFileResult, LoadFileHelper } from "../prebuiltPredict/LoadFileHelper";
import { ITableHelper, ITableState, TableHelper } from "../prebuiltPredict/tableHelper";
import PredictModelInfo from './PredictModelInfo';
import "./PredictPage.scss";
import PredictResult, { IAnalyzeModelInfo, ITableResultItem } from "./PredictResult";
import RegionalTable from "../../common/regionalTable";
import { ITag } from '../../model/applicationModels';
import Splitter, { SplitDirection } from '@devbookhq/splitter'

pdfjsLib.GlobalWorkerOptions.workerSrc = constants.pdfjsWorkerSrc(pdfjsLib.version);

export enum AnalyzedTagsMode {
    default = "default",
    LoadingRecentModel = "loadingRecentModel",
    ViewTable = "viewTable",
}

interface IPredictPageProps {
    tags: ITag[],
    analyzeResult: object,
    fields: object,
    file: File,
    fileUrl: string,
    updateFields: (updated_fields: any) => void;
    setHasUserChanges: (hasUserChanges: boolean, field: any) => void;
    markAsValidated: (field: string) => void;
    submitValidations: () => void;
}

interface IPredictPageState extends ILoadFileResult, ITableState {
    loadingRecentModel: boolean;
    isFetching: boolean;
    fetchedFileURL: string;
    fileLabel: string;
    fileChanged: boolean;
    file?: File;
    fileLoaded?: boolean;
    highlightedField: string;
    confirmDuplicatedAssetNameMessage?: string;
    imageAngle: number;
    viewTable?: boolean;
    viewRegionalTable?: boolean;
    regionalTableToView?: any;
    tableToView?: any;
    tableTagColor?: string;
    highlightedTableCellRowKey?: string;
    highlightedTableCellColumnKey?: string;
    imageUri: string,
    withPageRange: boolean;
    pageRange: string;
    pageRangeIsValid?: boolean;
    currentPage: number;
    numPages: number;
    imageWidth: number;
    imageHeight: number;
    tableIconTooltip: any;
    hoveringFeature: any;
    tableToViewId: string;
    shouldShowAlert: boolean;
    alertTitle: string;
    alertMessage: string;
}

export interface IModel {
    modelId: string;
    createdDateTime: string;
    lastUpdatedDateTime: string;
    status: string;
}

export default class PredictPage extends React.Component<IPredictPageProps, IPredictPageState> {
    private tableHelper: ITableHelper = new TableHelper(this);
    private fileHelper: ILoadFileHelper = new LoadFileHelper();

    public state: IPredictPageState = {
        loadingRecentModel: false,
        isFetching: false,
        fetchedFileURL: "",
        fileLabel: "",
        currentPage: 1,
        numPages: 1,
        imageUri: null,
        imageWidth: 0,
        imageHeight: 0,
        fileChanged: false,
        highlightedField: "",
        imageAngle: 0,
        viewTable: false,
        viewRegionalTable: false,
        regionalTableToView: null,
        tableTagColor: null,
        highlightedTableCellRowKey: null,
        highlightedTableCellColumnKey: null,

        tableIconTooltip: { display: "none", width: 0, height: 0, top: 0, left: 0 },
        hoveringFeature: null,
        tableToView: null,
        tableToViewId: null,

        withPageRange: false,
        pageRange: "",
        shouldShowAlert: false,
        alertTitle: "",
        alertMessage: "",
    };

    private imageMap: ImageMap;

    public async componentDidMount() {
        await this.loadFile(this.props.file);
    }

    public async componentDidUpdate(_prevProps: null, prevState: IPredictPageState) {
        if (this.state.imageUri) {
            if (prevState.currentPage !== this.state.currentPage) {
                this.fileHelper.loadPage(this.state.currentPage).then((res: any) => {
                    if (res) {
                        this.setState({ ...res });
                    }
                });
            }
            if (this.getOcrFromAnalyzeResult(this.props.analyzeResult).length > 0 &&
                prevState.imageUri !== this.state.imageUri) {
                this.drawPredictionResult();
            }

            if (prevState.highlightedField !== this.state.highlightedField) {
                this.setPredictedFieldHighlightStatus(this.state.highlightedField);
            }

            if (prevState.highlightedTableCellColumnKey !== this.state.highlightedTableCellColumnKey ||
                prevState.highlightedTableCellRowKey !== this.state.highlightedTableCellRowKey) {
                this.setPredictedFieldTableCellHighlightStatus(this.state.highlightedTableCellRowKey, this.state.highlightedTableCellColumnKey)
            }
        }
    }

    public render() {
        const modelInfo: IAnalyzeModelInfo = this.getAnalyzeModelInfo(this.props.analyzeResult);

        let tagViewMode: AnalyzedTagsMode;
        if (this.state.loadingRecentModel) {
            tagViewMode = AnalyzedTagsMode.LoadingRecentModel;
        } else if (this.state.viewRegionalTable) {
            tagViewMode = AnalyzedTagsMode.ViewTable;
        } else {
            tagViewMode = AnalyzedTagsMode.default;
        }

        return (
            <div
                className={`predict skipToMainContent`}
                id="pagePredict"
                style={{ display: "flex" }} >
                <Splitter direction={SplitDirection.Horizontal}>
                    <div className={"predict-sidebar bg-lighter-1"}>
                        <div className="condensed-list">
                            {tagViewMode === AnalyzedTagsMode.default &&
                                <>
                                    <>
                                        <div className="p-3" style={{ marginTop: "8px" }}>
                                            <PredictResult
                                                downloadPrefix="Analyze"
                                                predictions={this.props.fields}
                                                page={this.state.currentPage}
                                                tags={this.props.tags}
                                                downloadResultLabel={this.state.fileLabel}
                                                onPredictionMouseEnter={this.onPredictionMouseEnter}
                                                onPredictionMouseLeave={this.onPredictionMouseLeave}
                                                onTablePredictionClick={this.onTablePredictionClick}
                                                setHasUserChanges={this.props.setHasUserChanges}
                                            >
                                                <PredictModelInfo modelInfo={modelInfo} />
                                            </PredictResult>
                                            <button onClick={() => this.props.submitValidations()} className="btn btn-success mt-3">Submit Validations</button>
                                        </div>
                                    </>
                                </>
                            }
                            {tagViewMode === AnalyzedTagsMode.LoadingRecentModel &&
                                <Spinner className="loading-tag" size={SpinnerSize.large} />
                            }
                            {this.state.viewRegionalTable &&
                                <div className="m-2">
                                    <RegionalTable
                                        regionalTableToView={this.state.regionalTableToView}
                                        tableTagColor={this.state.tableTagColor}
                                        tags={this.props.tags}
                                        onMouseEnter={this.onMouseEnter}
                                        onMouseLeave={this.onMouseLeave}
                                        onRowEdit={this.editRowClick}
                                        onMarkAsValidated={this.markAsValidated}
                                    />
                                    <PrimaryButton
                                        className="mt-4 ml-2"
                                        theme={getPrimaryGreyTheme()}
                                        onClick={() => this.setState({ viewRegionalTable: false })}>Back</PrimaryButton>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="predict-main">
                        {this.state.imageUri && this.renderImageMap()}
                        {this.renderPrevPageButton()}
                        {this.renderNextPageButton()}
                        {this.renderPageIndicator()}
                    </div>
                </Splitter>
            </div>
        );
    }


    private editRowClick = (item: any, hasChanges: boolean) => {
        const { regionalTableToView } = this.state
        let new_elements = (this.props.fields as any)[regionalTableToView.fieldName].valueArray.map((el: any) => {
            if (el.id === item.id) {
                return { ...el, editable: !item.editable }
            }
            return el;
        })

        const updated_data = { ...(this.props.fields as any) }
        updated_data[regionalTableToView.fieldName].valueArray = new_elements;
        this.props.updateFields(updated_data);
        this.props.setHasUserChanges(hasChanges, item);
    }

    private loadFile = (file: File) => {
        this.setState({ isFetching: true });
        this.fileHelper.loadFile(file).then((res: ILoadFileResult) => {
            if (res) {
                this.setState({
                    ...res,
                    isFetching: false,
                    fileChanged: false
                })
            }
        });
    }

    onPageRangeChange = (withPageRange: boolean, pageRange: string) => {
        this.setState({ withPageRange, pageRange });
    }

    private renderPrevPageButton = () => {
        const prevPage = () => {
            this.setState((prevState) => ({
                currentPage: Math.max(1, prevState.currentPage - 1),
            }), () => {
                this.imageMap?.removeAllFeatures();
            });
        };

        if (this.state.currentPage > 1) {
            return (
                <IconButton
                    className="toolbar-btn prev"
                    title="Previous"
                    iconProps={{ iconName: "ChevronLeft" }}
                    onClick={prevPage}
                />
            );
        } else {
            return <div></div>;
        }
    }

    private renderNextPageButton = () => {
        const { currentPage, numPages } = this.state;
        const nextPage = () => {
            this.setState((prevState) => ({
                currentPage: Math.min(prevState.currentPage + 1, numPages),
            }), () => {
                this.imageMap?.removeAllFeatures();
            });
        };

        if (currentPage < numPages) {
            return (
                <IconButton
                    className="toolbar-btn next"
                    title="Next"
                    onClick={nextPage}
                    iconProps={{ iconName: "ChevronRight" }}
                />
            );
        } else {
            return <div></div>;
        }
    }
    private renderPageIndicator = () => {
        const { numPages } = this.state;
        return numPages > 1 ?
            <p className="page-number">
                Page {this.state.currentPage} of {numPages}
            </p> : <div></div>;
    }

    private renderImageMap = () => {
        const hostStyles: Partial<ITooltipHostStyles> = {
            root: {
                position: "absolute",
                top: this.state.tableIconTooltip.top,
                left: this.state.tableIconTooltip.left,
                width: this.state.tableIconTooltip.width,
                height: this.state.tableIconTooltip.height,
                display: this.state.tableIconTooltip.display,
            },
        };
        return (
            <div style={{ width: "100%", height: "100%" }}>
                <CanvasCommandBar
                    handleZoomIn={this.handleCanvasZoomIn}
                    handleZoomOut={this.handleCanvasZoomOut}
                    handleRotateImage={this.handleRotateCanvas}
                />
                <ImageMap
                    initEditorMap={true}
                    ref={(ref) => {
                        this.imageMap = ref;
                        this.tableHelper.setImageMap(ref);
                    }}
                    imageUri={this.state.imageUri || ""}
                    imageWidth={this.state.imageWidth}
                    imageHeight={this.state.imageHeight}
                    imageAngle={this.state.imageAngle}
                    featureStyler={this.featureStyler}
                    onMapReady={this.noOp}
                    tableBorderFeatureStyler={this.tableHelper.tableBorderFeatureStyler}
                    tableIconFeatureStyler={this.tableHelper.tableIconFeatureStyler}
                    tableIconBorderFeatureStyler={this.tableHelper.tableIconBorderFeatureStyler}
                    handleTableToolTipChange={this.tableHelper.handleTableToolTipChange}
                />
                <TooltipHost
                    content={"rows: " + this.state.tableIconTooltip.rows +
                        " columns: " + this.state.tableIconTooltip.columns}
                    id="tableInfo"
                    styles={hostStyles}
                >
                    <div
                        aria-describedby="tableInfo"
                        className="tooltip-container"
                        onClick={this.handleTableIconFeatureSelect}
                    />
                </TooltipHost>
                {this.state.tableToView !== null &&
                    <TableView
                        handleTableViewClose={this.handleTableViewClose}
                        tableToView={this.state.tableToView}
                    />
                }
            </div>
        );
    }

    private handleTableIconFeatureSelect = () => {
        if (this.state.hoveringFeature != null) {
            const tableState = this.imageMap.getTableBorderFeatureByID(this.state.hoveringFeature).get("state");
            if (tableState === "hovering" || tableState === "rest") {
                this.tableHelper.setTableToView(this.tableHelper.getTable(this.state.currentPage, this.state.hoveringFeature),
                    this.state.hoveringFeature);
            } else {
                this.closeTableView("hovering");
            }
        }
    }

    private handleTableViewClose = () => {
        this.closeTableView("rest");
    }

    private closeTableView = (state: string) => {
        if (this.state.tableToView) {
            this.tableHelper.setTableState(this.state.tableToViewId, state);
            this.setState({
                tableToView: null,
                tableToViewId: null,
            });
        }
    }

    private handleCanvasZoomIn = () => {
        this.imageMap.zoomIn();
    }

    private handleCanvasZoomOut = () => {
        this.imageMap.zoomOut();
    }

    private handleRotateCanvas = (degrees: number) => {
        this.setState({ imageAngle: this.state.imageAngle + degrees });
    }

    private createBoundingBoxVectorFeature = (text, boundingBox, imageExtent, ocrExtent) => {
        const coordinates: number[][] = [];

        // extent is int[4] to represent image dimentions: [left, bottom, right, top]
        const imageWidth = imageExtent[2] - imageExtent[0];
        const imageHeight = imageExtent[3] - imageExtent[1];
        const ocrWidth = ocrExtent[2] - ocrExtent[0];
        const ocrHeight = ocrExtent[3] - ocrExtent[1];

        for (let i = 0; i < boundingBox.length; i += 2) {
            coordinates.push([
                Math.round((boundingBox[i] / ocrWidth) * imageWidth),
                Math.round((1 - (boundingBox[i + 1] / ocrHeight)) * imageHeight),
            ]);
        }

        const feature = new Feature({
            geometry: new Polygon([coordinates]),
        });
        const tag = this.props.tags && this.props.tags.find((tag) => tag.name.toLocaleLowerCase() === text.toLocaleLowerCase());
        const isHighlighted = (text.toLocaleLowerCase() === this.state.highlightedField.toLocaleLowerCase());
        feature.setProperties({
            color: _.get(tag, "color", "#333333"),
            fieldName: text,
            isHighlighted,
        });

        return feature;
    }

    private createBoundingBoxVectorFeatureForTableCell = (text, boundingBox, imageExtent, ocrExtent, rowKey, columnKey) => {
        const coordinates: number[][] = [];

        // extent is int[4] to represent image dimentions: [left, bottom, right, top]
        const imageWidth = imageExtent[2] - imageExtent[0];
        const imageHeight = imageExtent[3] - imageExtent[1];
        const ocrWidth = ocrExtent[2] - ocrExtent[0];
        const ocrHeight = ocrExtent[3] - ocrExtent[1];

        for (let i = 0; i < boundingBox.length; i += 2) {
            coordinates.push([
                Math.round((boundingBox[i] / ocrWidth) * imageWidth),
                Math.round((1 - (boundingBox[i + 1] / ocrHeight)) * imageHeight),
            ]);
        }

        const feature = new Feature({
            geometry: new Polygon([coordinates]),
        });
        const tag = this.props.tags && this.props.tags.find((tag) => tag.name.toLocaleLowerCase() === text.toLocaleLowerCase());
        const isHighlighted = (text.toLocaleLowerCase() === this.state.highlightedField.toLocaleLowerCase() ||
            (this.state.highlightedTableCellRowKey === rowKey && this.state.highlightedTableCellColumnKey === columnKey));
        feature.setProperties({
            color: _.get(tag, "color", "#333333"),
            fieldName: text,
            isHighlighted,
            rowKey,
            columnKey,
        });

        return feature;
    }

    private featureStyler = (feature) => {
        return new Style({
            stroke: new Stroke({
                color: feature.get("color"),
                width: feature.get("isHighlighted") ? 4 : 2,
            }),
            fill: new Fill({
                color: "rgba(255, 255, 255, 0)",
            }),
        });
    }

    // here
    private drawPredictionResult = (): void => {
        this.imageMap?.removeAllFeatures();
        const features = [];
        const imageExtent = [0, 0, this.state.imageWidth, this.state.imageHeight];
        const ocrForCurrentPage: any = this.getOcrFromAnalyzeResult(this.props.analyzeResult)[this.state.currentPage - 1];
        const ocrExtent = [0, 0, ocrForCurrentPage && ocrForCurrentPage.width, ocrForCurrentPage && ocrForCurrentPage.height];
        const fields = this.getPredictionsFromAnalyzeResult(this.props.analyzeResult);

        Object.keys(fields).forEach((fieldName) => {
            const field = fields[fieldName];
            if (!field) {
                return;
            }
            if (field?.type === "object" && field.valueObject) {
                Object.keys(field?.valueObject).forEach((rowName, rowIndex) => {
                    if (field?.valueObject?.[rowName]) {
                        Object.keys(field?.valueObject?.[rowName]?.valueObject).forEach((columnName, colIndex) => {
                            const tableCell = field?.valueObject?.[rowName]?.valueObject?.[columnName];
                            if (tableCell?.page === this.state.currentPage) {
                                const text = fieldName;
                                const boundingbox = _.get(tableCell, "boundingBox", []);
                                const feature = this.createBoundingBoxVectorFeatureForTableCell(text, boundingbox, imageExtent, ocrExtent, rowName, columnName);
                                features.push(feature);
                            }
                        })
                    }
                })
            }
            else if (field.type === "array" && field.valueArray) {
                field?.valueArray.forEach((row, rowIndex) => {
                    Object.keys(row?.valueObject).forEach((columnName, colIndex) => {
                        const tableCell = field?.valueArray?.[rowIndex]?.valueObject?.[columnName];
                        if (tableCell?.page === this.state.currentPage) {
                            const text = fieldName;
                            const boundingbox = _.get(tableCell, "boundingBox", []);
                            const feature = this.createBoundingBoxVectorFeatureForTableCell(text, boundingbox, imageExtent, ocrExtent, "#" + rowIndex, columnName);
                            features.push(feature);
                        }
                    })
                })
            }
            else {
                if (_.get(field, "page", null) === this.state.currentPage) {
                    const text = fieldName;
                    const boundingbox = _.get(field, "boundingBox", []);
                    const feature = this.createBoundingBoxVectorFeature(text, boundingbox, imageExtent, ocrExtent);
                    features.push(feature);
                }
            }
        });
        this.imageMap?.addFeatures(features);
        this.tableHelper.drawTables(this.state.currentPage);
    }

    private getPredictionsFromAnalyzeResult(analyzeResult: any) {
        const fields = _.get(analyzeResult?.analyzeResult ? analyzeResult?.analyzeResult : analyzeResult, "documentResults[0].fields", {});
        return fields;
    }

    private getAnalyzeModelInfo(analyzeResult) {
        const { modelId, docType, docTypeConfidence } = _.get(analyzeResult, "documentResults[0]", {})
        return { modelId, docType, docTypeConfidence };
    }

    private getOcrFromAnalyzeResult(analyzeResult: any) {
        return _.get(analyzeResult?.analyzeResult ? analyzeResult?.analyzeResult : analyzeResult, "readResults", []);
    }

    private noOp = () => {
        // no operation
    }

    private onTablePredictionClick = (predictedItem: ITableResultItem, tagColor: string) => {
        this.setState({ viewRegionalTable: true, regionalTableToView: predictedItem, tableTagColor: tagColor });
    }

    private markAsValidated = (field) => {
        this.setState({ viewRegionalTable: false, regionalTableToView: "" })
        this.props.markAsValidated(field)
    }

    private onPredictionMouseEnter = (predictedItem: any) => {
        this.setState({
            highlightedField: predictedItem.fieldName ?? "",
        });
    }

    private onPredictionMouseLeave = (predictedItem: any) => {
        this.setState({
            highlightedField: "",
        });
    }

    private setPredictedFieldHighlightStatus = (highlightedField: string) => {
        const features = this.imageMap.getAllFeatures();
        for (const feature of features) {
            if (feature.get("fieldName").toLocaleLowerCase() === highlightedField.toLocaleLowerCase()) {
                feature.set("isHighlighted", true);
            } else {
                feature.set("isHighlighted", false);
            }
        }
    }

    private setPredictedFieldTableCellHighlightStatus = (highlightedTableCellRowKey: string, highlightedTableCellColumnKey: string) => {
        const features = this.imageMap.getAllFeatures();
        for (const feature of features) {
            if (highlightedTableCellRowKey && highlightedTableCellColumnKey && feature.get("rowKey")?.toLocaleLowerCase() === highlightedTableCellRowKey?.toLocaleLowerCase() &&
                feature.get("columnKey")?.toLocaleLowerCase() === highlightedTableCellColumnKey?.toLocaleLowerCase()) {
                feature.set("isHighlighted", true);
            } else {
                feature.set("isHighlighted", false);
            }
        }
    }

    private onMouseEnter = (rowName: string, columnName: string) => {
        this.setState({ highlightedTableCellRowKey: rowName, highlightedTableCellColumnKey: columnName });
    }

    private onMouseLeave = () => {
        this.setState({ highlightedTableCellRowKey: null, highlightedTableCellColumnKey: null });
    }
}
